<template>
    <div class="hp-10 relative">
        <!-- MAP -->
        <div class="flex" id="map" style="border: 1px solid #dedede; height: 100%" ref="map"></div>

        <!-- Additional controls -->
        <div class="leaflet-touch map-additional-control">
            <!-- Filters on relation type -->
            <div v-if="canFilter.length" class="leaflet-bottom leaflet-left">
                <el-checkbox-group
                    :value="checkedFilter"
                    class="leaflet-bar leaflet-control p-2 white-bkg"
                >
                    <ul>
                        <li v-for="filter in filters" :key="filter.id">
                            <el-checkbox
                                class="py-1 flex-level"
                                :label="filter.id"
                                @change="filterMarkers($event, filter.id)"
                                :disabled="!hasType(filter.id)"
                            >
                                <SaasIcon
                                    icon="svgMarkerMap"
                                    :width="7"
                                    :height="11"
                                    class="mr-1"
                                    :style="{ color: `var(${filter.bgcolor})` }"
                                />
                                <span>{{ filter.label }}</span>
                            </el-checkbox>
                        </li>
                    </ul>
                </el-checkbox-group>
            </div>
            <!-- loader during markers charging  -->
            <transition name="fade">
                <div
                    v-if="currentPage > 0 && lastPage > 1 && !allMarkersLoaded"
                    class="leaflet-top leaflet-center marker-loader"
                >
                    <div class="leaflet-bar leaflet-control p-2 white-bkg flex-level jc-space">
                        <div class="marker-loader-icon">
                            <span v-if="percentage < 100" v-loading="true"></span>
                            <i v-else class="el-icon-circle-check success-50"></i>
                        </div>
                        <span class="percentage">{{ percentage }} %</span>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

require('leaflet.markercluster/dist/MarkerCluster.css');
require('leaflet.markercluster/dist/MarkerCluster.Default.css');
require('leaflet.markercluster/dist/leaflet.markercluster.js');

import TagHumansConfig from 'Config/authenticated/TagHumans';
import MapService from 'Services/MapService';

import { MapGroupBy, MapGetByKeys } from 'utils/object';

export default {
    components: {
        SaasIcon,
    },
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        maptoken: {
            type: String,
            required: true,
        },
        centermap: {
            type: Object,
            required: false,
            default: () => {},
        },
    },
    data() {
        return {
            map: null,
            cluster: null,
            defaultCentermap: {
                lat: 48.85341,
                lng: 2.3488,
            },
            canFilter: ['client', 'supplier', 'prospect'],
            typePresent: [],
            checkedFilter: [],
            allMarkersMap: new Map(),
            currentPage: 0,
            lastPage: 1,
            allMarkersLoaded: false,
        };
    },

    mounted() {
        this.checkedFilter = this.canFilter;
        this.init();
    },

    computed: {
        /**
         * always used ?
         */
        filterDisplay() {
            return this.$store.state[this.listinghash].display.filters;
        },
        /**
         * Returns an array of tagHumans that can be filtered on the map
         */
        filters() {
            return Object.entries(TagHumansConfig)

                .map(([id, tag]) => ({ id, ...tag })) // instruction can be remove if we add directly id for each tag human
                .filter(({ id }) => this.canFilter.includes(id));
        },
        /**
         * Calculate the percentage of markers loaded
         */
        percentage() {
            return Math.round((this.currentPage / this.lastPage) * 100);
        },
    },

    watch: {
        /**
         * always used ?
         */
        filterDisplay: function () {
            this.map.invalidateSize();
        },
    },

    methods: {
        resetMarkers() {
            this.cluster.clearLayers();
        },

        initCluster() {
            this.cluster = new L.MarkerClusterGroup({
                iconCreateFunction: function (cluster) {
                    const markers = [
                        ...new Set(
                            cluster
                                .getAllChildMarkers()
                                .map((marker) => marker.options.relationType),
                        ),
                    ];

                    const classes =
                        markers.length === 1 ? `cluster cluster--${markers[0]}` : 'cluster';

                    return L.divIcon({
                        html: cluster.getChildCount(),
                        className: classes,
                        iconSize: null,
                    });
                },
                polygonOptions: {
                    opacity: 0,
                    fillOpacity: 0,
                },
            });
        },

        initMap() {
            this.map = L.map(this.$refs.map);

            let center = {
                lat: this.centermap.lat
                    ? parseFloat(this.centermap.lat)
                    : this.defaultCentermap.lat,
                lng: this.centermap.lng
                    ? parseFloat(this.centermap.lng)
                    : this.defaultCentermap.lng,
            };

            this.map.setView(Object.values(center), 4);

            this.map.attributionControl.addAttribution(
                '<a href="https://www.jawg.io" target="_blank">&copy; Jawg</a> - <a href="https://www.openstreetmap.org" target="_blank">&copy; OpenStreetMap</a>&nbsp;contributors',
            );
            L.tileLayer(MapService.getTileLayer(this.maptoken), {}).addTo(this.map);

            delete L.Icon.Default.prototype._getIconUrl;

            L.Icon.Default.mergeOptions({
                iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png').default,
                iconUrl: require('leaflet/dist/images/marker-icon.png').default,
                shadowUrl: require('leaflet/dist/images/marker-shadow.png').default,
            });
        },

        /**
         * Add new markers to map (can be called many time if map contain a lot of markers then pagination is used by backend)
         *
         * @param {Object} newMarkers - new markers added to map
         * @return {void}
         */
        onFetchMarker({ rowset: newMarkers, pagination }) {
            this.updateLoadingMarkers(pagination);

            const chargedMarkers = [...this.allMarkersMap.values()].flat();
            this.allMarkersMap = MapGroupBy(
                [...chargedMarkers, ...newMarkers],
                (marker) => marker.options.relationType,
            );

            this.typePresent = [...this.allMarkersMap.keys()];

            // Add only the markers that should be visible on the map
            const newMarkersVisible = newMarkers.filter(({ options: { relationType } }) =>
                this.checkedFilter.includes(relationType),
            );
            this.cluster.addLayers(newMarkersVisible);
        },

        resetZoom() {
            let bounds = this.cluster.getBounds();
            if (Object.values(bounds).length > 0) {
                this.map.fitBounds(bounds, { padding: [0, 0] });
            }
        },

        init() {
            this.initCluster();
            this.initMap();
            this.map.addLayer(this.cluster);

            window.EventBus.on('listing:map:fetch', this.onFetchMarker);
            window.EventBus.on('listing:map:reset', this.resetMarkers);
            window.EventBus.on('listing:map:resetZoom', this.resetZoom);
        },

        /**
         * Add or Remove markers according to checkbox
         * Management of the checkedFilter array here rather than with v-model
         * to avoid bug in case of double click on the checkbox
         *
         * @param {Boolean} checked - Event who determine if item is checked or not
         * @param {String} relationType - name of relation type who determine which markers remove or add
         * @return {void}
         */
        filterMarkers(checked, relationType) {
            const markers = MapGetByKeys(this.allMarkersMap, relationType);

            if (checked) {
                this.checkedFilter = [...this.checkedFilter, relationType];
                this.cluster.addLayers(markers);
            } else {
                this.checkedFilter = this.checkedFilter.filter((filter) => filter !== relationType);
                this.cluster.removeLayers(markers);
            }
        },

        /**
         * Return true if one marker has relation type like idType
         *
         * @param {String} idType - (client | prospect | supplier)
         * @return {Boolean}
         */
        hasType(idType) {
            return this.typePresent.includes(idType);
        },

        /**
         * Update props needed to display or not the percentage loader of all markers
         *
         * @param {Object} pagination - current_page and last_page prop of pagination needed for calculate percentage
         * @return {void}
         */
        updateLoadingMarkers({ current_page, last_page }) {
            this.currentPage = current_page;
            this.lastPage = last_page;

            this.currentPage === this.lastPage
                ? setTimeout(() => (this.allMarkersLoaded = true), 2500)
                : (this.allMarkersLoaded = false);
        },
    },
    beforeDestroy() {
        window.EventBus.off('listing:map:fetch', this.onFetchMarker);
        window.EventBus.off('listing:map:reset', this.resetMarkers);
        window.EventBus.off('listing:map:resetZoom', this.resetZoom);
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

#map.leaflet-container {
    font: inherit !important;

    a.leaflet-popup-close-button {
        font-family: inherit;
        font-weight: inherit;
        padding: 0;
        display: inline-flex;
        justify-content: flex-end;
        align-items: flex-start;
        font-size: $extra-big-font-size;
        margin-top: spacer(2);
        margin-right: spacer(2);
    }

    .tag-relationType {
        border-radius: $val-3;
    }
}
#map {
    .leaflet-bottom {
        pointer-events: auto !important;
    }
    .el-checkbox__inner {
        height: 16px !important;
        width: 16px !important;
        &:after {
            border-width: 2px !important;
            left: 4.5px !important;
        }
    }
    .el-checkbox__label {
        font-size: 12px !important;
        line-height: 16px !important;
        display: flex !important;
        align-items: center;
    }
}
.map-additional-control {
    .leaflet-center.marker-loader {
        left: calc((100% - 74px) / 2);

        .leaflet-bar {
            width: 74px;
            height: 33px;
            border: none;
            box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.16);

            .marker-loader-icon,
            .el-icon-circle-check,
            .el-loading-parent--relative,
            .el-loading-spinner .circular {
                width: 17px;
                height: 17px;
                font-size: 17px;
            }
            .el-loading-mask {
                background-color: $white;
            }
            .el-loading-spinner {
                top: auto;
                margin-top: 0;
                width: 17px;
            }
            .percentage {
                font-size: $font-size;
                font-weight: $font-weight-semibold;
            }
        }
    }
    .fade-enter-active {
        transition: all 250ms ease;
    }
}

.cluster {
    background-color: var(--colors-component-background-information-tag-directory-company);
    border: 1px solid var(--colors-component-border-tag-directory-company);
    color: var(--colors-component-text-information-tag-directory-company);

    &--contact {
        background-color: var(--colors-component-background-information-tag-directory-contact);
        border-color: var(--colors-component-border-tag-directory-contact);
        color: var(--colors-component-text-tag-directory-contact);
    }

    &--client {
        background-color: var(--colors-component-background-information-tag-directory-customer);
        border-color: var(--colors-component-border-tag-directory-customer);
        color: var(--colors-component-text-tag-directory-customer);
    }

    &--prospect {
        background-color: var(--colors-component-background-information-tag-directory-prospect);
        border-color: var(--colors-component-border-tag-directory-prospect);
        color: var(--colors-component-text-tag-directory-prospect);
    }

    &--supplier {
        background-color: var(--colors-component-background-information-tag-directory-supplier);
        border-color: var(--colors-component-border-tag-directory-supplier);
        color: var(--colors-component-text-tag-directory-supplier);
    }

    &--company {
        background-color: var(--colors-component-background-information-tag-directory-company);
        border-color: var(--colors-component-border-tag-directory-company);
        color: var(--colors-component-text-tag-directory-company);
    }

    &--individual {
        background-color: var(--colors-component-background-information-tag-directory-individual);
        border-color: var(--colors-component-border-tag-directory-individual);
        color: var(--colors-component-text-tag-directory-individual);
    }

    &--archived {
        background-color: var(--colors-component-background-information-tag-directory-archive);
        border-color: var(--colors-component-border-tag-directory-archive);
        color: var(--colors-component-text-tag-directory-archive);
    }
}

.tag-third-map {
    border: 1px solid;
    border-radius: 44px;
    display: inline-block;
    font-family: var(--font-families-proxima-nova);
    margin: var(--spacing-0) 0 0;
    padding: 0 var(--spacing-1);

    &--contact {
        background-color: var(--colors-component-background-information-tag-directory-contact);
        border-color: var(--colors-component-border-tag-directory-contact);
        color: var(--colors-component-text-tag-directory-contact);
    }

    &--client {
        background-color: var(--colors-component-background-information-tag-directory-customer);
        border-color: var(--colors-component-border-tag-directory-customer);
        color: var(--colors-component-text-information-tag-directory-customer);
    }

    &--prospect {
        background-color: var(--colors-component-background-information-tag-directory-prospect);
        border-color: var(--colors-component-border-tag-directory-prospect);
        color: var(--colors-component-text-information-tag-directory-prospect);
    }

    &--supplier {
        background-color: var(--colors-component-background-information-tag-directory-supplier);
        border-color: var(--colors-component-border-tag-directory-supplier);
        color: var(--colors-component-text-information-tag-directory-supplier);
    }

    &--company {
        background-color: var(--colors-component-background-information-tag-directory-company);
        border-color: var(--colors-component-border-tag-directory-company);
        color: var(--colors-component-text-information-tag-directory-company);
    }

    &--individual {
        background-color: var(--colors-component-background-information-tag-directory-individual);
        border-color: var(--colors-component-border-tag-directory-individual);
        color: var(--colors-component-text-information-tag-directory-individual);
    }

    &--archived {
        background-color: var(--colors-component-background-information-tag-directory-archive);
        border-color: var(--colors-component-border-tag-directory-archive);
        color: var(--colors-component-text-information-tag-directory-archive);
    }
}
</style>
